import React, { useMemo, useRef, useState } from "react";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { CatalogItemFormModal } from "@components/items/CatalogItemFormModal";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { PlusIcon } from "@icons";
import { useCatalogItems } from "@api/catalogItem.api";
import { buildCatalogItemCreatePrefill } from "./buildCatalogItemCreatePrefill";
import { pickerHasHighlightedOption } from "./catalogItemPickerHighlight";
import { formatMoney } from "./formatMoney";
import { firstNonEmptyString } from "./nativeInvoiceFormHelpers";
import {
    buildCatalogItemPickerRows,
    selectionForCatalogItemPickerValue,
    toCatalogItemSelection,
    type CatalogItemPickerRow,
    type CatalogItemPricePickerRow,
    type CatalogItemSelection,
} from "./catalogItemSelection.helpers";
import styles from "./CatalogItemCombobox.module.scss";

export type { CatalogItemSelection } from "./catalogItemSelection.helpers";

interface Props {
    valueLabel?: string;
    initialSearch?: string;
    entityId?: number;
    currency?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    onSelect: (selection: CatalogItemSelection) => void;
    // One-off item: the typed text becomes the line's item name without
    // creating a catalog item (catalog creation stays on the footer button).
    onCommitFreeText: (text: string) => void;
    onOpenChange?: (open: boolean) => void;
    initialOpen?: boolean;
    linePrefill?: {
        productOrService: string;
        description: string;
        unitAmount: number | null;
        classIds: string[];
        accrualAccountId: string | null;
        receivablesAccountId: string | null;
    };
}

const nonEmptyText = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    return trimmed ?? undefined;
};

const toPricePickerItem = (
    priceRow: CatalogItemPricePickerRow,
): PickerItemData<string> => ({
    value: priceRow.value,
    label: formatMoney(priceRow.price.unitAmount),
    description: nonEmptyText(priceRow.price.description),
    rightText: priceRow.price.isDefault ? "Default" : undefined,
});

const toPickerItem = (row: CatalogItemPickerRow): PickerItemData<string> => {
    const description = nonEmptyText(row.item.description);
    if (row.priceRows) {
        return {
            value: row.value,
            label: row.item.name,
            description,
            rightText: `${row.priceRows.length} prices`,
            children: row.priceRows.map(toPricePickerItem),
            flyout: {
                searchable: true,
                searchPlaceholder: "Search prices",
            },
        };
    }
    return {
        value: row.value,
        label: row.item.name,
        description,
        rightText: row.price ? formatMoney(row.price.unitAmount) : undefined,
    };
};

const matchesQuery = (
    name: string,
    description: string | null,
    query: string,
) => {
    const searchLower = query.toLowerCase();
    return (
        name.toLowerCase() === searchLower ||
        (description?.toLowerCase() ?? "") === searchLower
    );
};

export const CatalogItemCombobox: React.FC<Props> = ({
    valueLabel,
    initialSearch,
    entityId,
    currency = "usd",
    inputRef,
    onSelect,
    onCommitFreeText,
    onOpenChange,
    initialOpen = false,
    linePrefill,
}) => {
    const label = valueLabel ?? "";
    const initialText = firstNonEmptyString(initialSearch, valueLabel);
    const [typedQuery, setTypedQuery] = useState(
        initialText.trim() !== label.trim() ? initialText.trim() : "",
    );
    const [search, setSearch] = useState<string>();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createPrefill, setCreatePrefill] = useState(() =>
        buildCatalogItemCreatePrefill({
            typedName: "",
            productOrService: "",
            description: "",
            unitAmount: null,
            classIds: [],
            accrualAccountId: null,
            receivablesAccountId: null,
            currency,
        }),
    );
    const isModalFlowRef = useRef(false);
    const didCreateRef = useRef(false);
    const didCommitRef = useRef(false);
    const didEscapeRef = useRef(false);
    const latestInputRef = useRef(initialText);

    useDebouncedEffect(
        () => setSearch(typedQuery || undefined),
        [typedQuery],
        SEARCH_DEBOUNCE_TIME,
    );

    const entityIds = useMemo(
        () => (entityId !== undefined ? [entityId] : undefined),
        [entityId],
    );

    const { items } = useCatalogItems({
        search: search ?? undefined,
        entityIds,
    });
    const rows = useMemo(
        () => buildCatalogItemPickerRows(items, currency),
        [currency, items],
    );
    const pickerItems = useMemo(() => rows.map(toPickerItem), [rows]);

    const handleOpenCreateModal = (name: string) => {
        isModalFlowRef.current = true;
        didCreateRef.current = false;
        setCreatePrefill(
            buildCatalogItemCreatePrefill({
                typedName: name,
                productOrService: linePrefill?.productOrService ?? "",
                description: linePrefill?.description ?? "",
                unitAmount: linePrefill?.unitAmount ?? null,
                classIds: linePrefill?.classIds ?? [],
                accrualAccountId: linePrefill?.accrualAccountId ?? null,
                receivablesAccountId: linePrefill?.receivablesAccountId ?? null,
                currency,
            }),
        );
        setIsCreateModalOpen(true);
    };

    const commitFreeText = () => {
        didCommitRef.current = true;
        onCommitFreeText(latestInputRef.current.trim());
    };

    const handleKeyDownCapture = (
        event: React.KeyboardEvent<HTMLDivElement>,
    ) => {
        if (event.key === "Escape") {
            didEscapeRef.current = true;
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" || event.defaultPrevented) {
            return;
        }
        const hasHighlightedOption = pickerHasHighlightedOption(
            event.currentTarget,
        );
        if (hasHighlightedOption) {
            if (isModalFlowRef.current) {
                event.stopPropagation();
            }
            return;
        }
        if (latestInputRef.current.trim() === label.trim()) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        commitFreeText();
    };

    return (
        <>
            <div
                className={styles.picker}
                onKeyDownCapture={handleKeyDownCapture}
                onKeyDown={handleKeyDown}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <Picker.Select
                    items={pickerItems}
                    nested="flyout"
                    trigger="cellInput"
                    chevron="none"
                    filterable={false}
                    ref={inputRef}
                    className={styles.cellInput}
                    placeholder="Find or add item"
                    defaultOpen={initialOpen}
                    defaultInputValue={initialText}
                    emptyLabel="No matching items"
                    onInputValueChange={(query) => {
                        latestInputRef.current = query;
                        setTypedQuery(
                            query.trim() !== label.trim() ? query.trim() : "",
                        );
                    }}
                    onValueChange={(value) => {
                        if (value === null) {
                            return;
                        }
                        const selection = selectionForCatalogItemPickerValue(
                            rows,
                            value,
                            currency,
                        );
                        if (!selection) {
                            return;
                        }
                        didCommitRef.current = true;
                        onSelect(selection);
                    }}
                    onOpenChange={(open) => {
                        if (open) {
                            didEscapeRef.current = false;
                            onOpenChange?.(open);
                            return;
                        }
                        if (isModalFlowRef.current) {
                            return;
                        }
                        if (
                            !didCommitRef.current &&
                            !didEscapeRef.current &&
                            latestInputRef.current.trim() !== label.trim()
                        ) {
                            commitFreeText();
                            return;
                        }
                        didEscapeRef.current = false;
                        onOpenChange?.(open);
                    }}
                    renderFooter={(query) => {
                        const trimmed = query.trim();
                        const isNewName =
                            trimmed !== "" &&
                            trimmed !== label.trim() &&
                            !items.some((item) =>
                                matchesQuery(
                                    item.name,
                                    item.description,
                                    trimmed,
                                ),
                            );
                        return (
                            <Picker.Item
                                leading={<PlusIcon />}
                                onSelect={() =>
                                    handleOpenCreateModal(
                                        isNewName ? trimmed : "",
                                    )
                                }
                            >
                                {isNewName ? `Create "${trimmed}"` : "Add item"}
                            </Picker.Item>
                        );
                    }}
                />
            </div>
            <CatalogItemFormModal
                show={isCreateModalOpen}
                presentation="drawer"
                initialName={createPrefill.name}
                initialDescription={createPrefill.description}
                initialClassIds={createPrefill.classIds}
                initialRevenueAccountId={createPrefill.revenueAccountId}
                initialReceivablesAccountId={createPrefill.receivablesAccountId}
                initialUnitAmount={createPrefill.unitAmount}
                initialCurrency={createPrefill.currency}
                initialEntityId={entityId}
                onCreated={(item) => {
                    didCreateRef.current = true;
                    didCommitRef.current = true;
                    setIsCreateModalOpen(false);
                    isModalFlowRef.current = false;
                    onSelect(toCatalogItemSelection(item, currency));
                }}
                onHide={() => {
                    setIsCreateModalOpen(false);
                    isModalFlowRef.current = false;
                    if (didCreateRef.current) {
                        didCreateRef.current = false;
                        return;
                    }
                    onOpenChange?.(false);
                }}
            />
        </>
    );
};
